import { useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Box, Eye, EyeOff } from 'lucide-react'
import api from '../../services/api'

export default function ResetPassword() {
  const { token } = useParams()
  const [params] = useSearchParams()
  const emailFromQuery = params.get('email') || ''
  const navigate = useNavigate()

  const [email, setEmail] = useState(emailFromQuery)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    if (password !== confirm) { setError('Passwords do not match.'); return }
    setLoading(true); setError('')
    try {
      await api.post(`/reset-password/${token}`, {
        email,
        password,
        password_confirmation: password,
      })
      setSuccess(true)
      setTimeout(() => navigate('/login'), 1500)
    } catch (err) {
      setError(err.response?.data?.message || 'Could not reset password.')
    } finally { setLoading(false) }
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-8 hover:opacity-80 transition-opacity" aria-label="Go to Stovalt home">
          <div className="w-9 h-9 bg-brand-500 rounded-xl flex items-center justify-center">
            <Box size={18} className="text-white" />
          </div>
          <span className="font-display font-700 text-white text-xl">Stovalt</span>
        </Link>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <h1 className="font-display text-xl font-700 text-white mb-1">Choose a new password</h1>

          {success ? (
            <div className="px-3 py-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-sm text-emerald-300">
              Password reset. Redirecting to sign in...
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 mt-3 px-3 py-2.5 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-300">
                  {error}
                </div>
              )}
              <form onSubmit={submit} className="space-y-3 mt-3">
                <div>
                  <label className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="block w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5">New password</label>
                  <div className="relative">
                    <input
                      type={showPw ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      className="block w-full pr-9 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
                    />
                    <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" tabIndex={-1}>
                      {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5">Confirm new password</label>
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={8}
                    className="block w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
                  />
                </div>
                <button type="submit" disabled={loading} className="w-full px-5 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold disabled:opacity-50">
                  {loading ? 'Saving...' : 'Reset password'}
                </button>
              </form>
            </>
          )}

          <p className="text-center text-xs text-slate-400 mt-5">
            <Link to="/login" className="text-brand-400 hover:text-brand-300">Back to sign in</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
